import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    CircularProgress,
} from '@material-ui/core';
import { createTemplate } from '../core/services/templates.service';

interface SaveTemplateDialogProps {
    open: boolean;
    entries: any[];
    userId: string;
    onSaved: () => void;
    onClose: () => void;
}

export const SaveTemplateDialog: React.FC<SaveTemplateDialogProps> = ({
    open,
    entries,
    userId,
    onSaved,
    onClose,
}) => {
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        setSaving(true);
        setError('');
        try {
            await createTemplate(userId, trimmed, entries);
            setName('');
            onSaved();
        } catch {
            setError('Failed to save template.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Save as template</DialogTitle>
            <DialogContent>
                <Typography variant="body2" style={{ marginBottom: 16, color: '#64748b' }}>
                    Save {entries.length} {entries.length === 1 ? 'entry' : 'entries'} as a reusable template. Dates and IDs are stripped.
                </Typography>

                <TextField
                    label="Template name"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                />

                {entries.length > 0 && (
                    <Box style={{ marginTop: 16, maxHeight: 200, overflowY: 'auto' }}>
                        {entries.map((e: any, i: number) => {
                            const title = String(e.title || '');
                            const amount = Number(e.amount || 0).toFixed(2);
                            const type = String(e.type || '');
                            return (
                                <Box key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' }}>
                                    <span>{title}</span>
                                    <span style={{ color: type === 'CREDIT' ? '#16a34a' : '#dc2626' }}>{amount}</span>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {error && (
                    <Box style={{ marginTop: 12 }}>
                        <Typography variant="body2" style={{ color: '#dc2626' }}>{error}</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={saving || !name.trim()}>
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
